import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Joi from 'joi';
import isEqual from 'lodash/isEqual';
import clsx from 'clsx';

import { useGetTunnelMessagesQuery, useLazyGetCurrentUserQuery } from 'api/main';
import { useSendUserKnowledgeMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import {
    extractSectionAndQuestion,
    getMessageByFundType,
    isStatusOk,
    jsonParseSafe,
    validateSchema,
} from 'utils/helpers';
import { texts } from 'utils/languages/fr';
import { fundTypes } from 'utils/constants';

import { TunnelStep, FormBox } from '../common';
import {
    CustomTextField,
    ErrorMessage,
    SubmitButton,
    InfoButton,
    Loader,
    RadioLabel,
    CheckboxLabel,
} from 'components';
import { Box, FormControl, FormGroup, RadioGroup, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTunnelChangeHandlers } from 'utils/hooks';

const useStyles = makeStyles(theme => ({
    form: {
        maxWidth: 660,
        width: '100%',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    flexContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    label: {
        marginRight: 10,
        marginBottom: 0,
        whiteSpace: 'break-spaces',
        width: 165,
        alignSelf: 'flex-start',
        [theme.breakpoints.down('675')]: {
            width: '100%',
        },
    },
    questionLabel: {
        display: 'block',
        fontSize: 16,
        letterSpacing: 0,
        marginBottom: 20,
    },
    option: {
        fontSize: 16,
        color: theme.palette.grey[600],
        alignSelf: 'center',
    },
    otherInput: {
        margin: 0,
    },
    instrumentsContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        [theme.breakpoints.down(675)]: {
            flexDirection: 'column',
            marginBottom: 10,
        },
    },
    optionsContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignSelf: 'flex-start',
        [theme.breakpoints.down(500)]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    question: {
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            alignItems: 'flex-start',
            marginTop: 10,
        },
    },
    withTopPadding: {
        paddingTop: 9,
    },
}));

const getUserKnowledgeQuestions = (data, tunnelSubscription) => {
    if (!data) return [];
    const stepKey = 'step35';
    const stepKeys = Object.keys(data).filter(key => key.includes(stepKey));
    const messages = stepKeys.map(key => ({
        key,
        value: data[key],
    }));

    const isFPCI = tunnelSubscription?.investmentFund?.type === fundTypes.FPCI;
    const isFPS = tunnelSubscription?.investmentFund?.type === fundTypes.FPS;
    const isSLP = tunnelSubscription?.investmentFund?.type === fundTypes.SLP;
    const fundText = getMessageByFundType(data, `${stepKey}.group_title`, isFPS, isSLP, isFPCI);

    const sectionQuestions = [
        [1, 2, 3, 4],
        [5, 6, 7, 8],
        [9, 15],
    ];

    const sections = [
        { key: `${stepKey}.group_1`, value: `${fundText} 1/3`, questions: [] },
        { key: `${stepKey}.group_2`, value: `${fundText} 2/3`, questions: [] },
        { key: `${stepKey}.group_3`, value: `${fundText} 3/3`, questions: [] },
    ];

    const question9Rows = [];
    const featuresKey = 'general_options.features';
    const risksKey = 'general_options.risks';
    const trueKey = 'general_options.option_true';
    const falseKey = 'general_options.option_false';
    const unknownKey = 'general_options.unknown';
    const otherKey = 'general_options.other_2';

    messages.forEach(msg => {
        const parts = msg.key.split('.');
        parts?.shift();
        const questionName = parts?.shift();
        const questionIndex = +questionName.replace('question_', '');

        const sectionIndex = sectionQuestions.findIndex(s => s.includes(questionIndex));
        const section = sections[sectionIndex];

        if (section) {
            const element = parts?.shift();
            if (element.includes('label')) {
                section.questions.push({
                    key: `${section.key}.${questionName}`,
                    value: msg.value,
                    options: [],
                    isSLP: element.includes('slp_pp'),
                    isFPS: element.includes('fps'),
                });
            } else if (element.includes('option_')) {
                const existingQuestions = section.questions.filter(q =>
                    q.key.includes(questionName)
                );
                existingQuestions?.forEach(existingQuestion => {
                    existingQuestion.options.push(msg);
                });
            }
        } else if (!questionName.includes('description') && !questionName.includes('group_title')) {
            let infoTitle = '';
            let infoContent = '';
            if (questionName === 'question_12') {
                infoTitle = texts.ORGANISME_DE_PLACEMENT;
                infoContent = texts.PRODUIT_FINANCIER_AYANT;
            } else if (questionName === 'question_13') {
                infoTitle = texts.PLACEMENT_IMMOBILIER;
                infoContent = texts.PLACEMENT_COLLECTIF;
            }
            question9Rows.push({
                key: `${stepKey}.group_3.${questionName}`,
                value: msg.value,
                infoTitle,
                infoContent,
                elements: [
                    {
                        parent: questionName,
                        value: data[featuresKey],
                        key: featuresKey,
                    },
                    {
                        parent: questionName,
                        value: data[risksKey],
                        key: risksKey,
                    },
                ],
            });
        }
    });

    sections.forEach(section => {
        section.questions.forEach(question => {
            if (question.key === 'step35.group_3.question_9') {
                question.rows = question9Rows;
            } else if (question.key === 'step35.group_3.question_15') {
                question.options.push({ value: data[otherKey], key: otherKey });
            } else if (
                question.key !== 'step35.group_1.question_3' &&
                question.key !== 'step35.group_3.question_15'
            ) {
                question.options.push({ value: data[trueKey], key: trueKey });
                question.options.push({ value: data[falseKey], key: falseKey });
                question.options.push({ value: data[unknownKey], key: unknownKey });
            }
        });
    });

    const filteredSections = sections.map(section => {
        const filteredQuestions = new Set();
        section.questions.forEach(question => {
            const sameKeyQuestions = section.questions.filter(q => q.key === question.key);
            const slpQuestion = sameKeyQuestions.find(q => q.isSLP) || null;
            const fpsQuestion = sameKeyQuestions.find(q => q.isFPS) || null;
            const fpciQuestion = sameKeyQuestions.find(q => !q.isSLP && !q.isFPS) || null;
            if (isSLP && slpQuestion) {
                filteredQuestions.add(slpQuestion);
            } else if (isFPS && fpsQuestion) {
                filteredQuestions.add(fpsQuestion);
            } else {
                filteredQuestions.add(fpciQuestion);
            }
        });
        return { ...section, questions: [...filteredQuestions] };
    });

    return filteredSections;
};

const UserKnowledge = ({ userStep, handleNextStep, roleName }) => {
    const classes = useStyles();

    const setTunnelChangesHandlers = useTunnelChangeHandlers();
    const { data: messages } = useGetTunnelMessagesQuery();
    const [getCurrentUser, { isFetching: userLoading }] = useLazyGetCurrentUserQuery();
    const [sendUserKnowledge, { isLoading: buttonLoader }] = useSendUserKnowledgeMutation();

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const sections = useMemo(
        () => getUserKnowledgeQuestions(messages, tunnelSubscription),
        [messages, tunnelSubscription]
    );
    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);
    const hasOnlyFailedSubmits = useMemo(
        () => userStep?.failedAttemptsNumber > 0 && userStep?.successfulAttemptsNumber === 0,
        [userStep]
    );
    const hasSubmittedOnce = isResubmit || hasOnlyFailedSubmits;
    const isCompany = useMemo(() => roleName.includes('PM'), [roleName]);
    const isSLP = tunnelSubscription?.investmentFund?.type === fundTypes.SLP;

    const userKnowledge = useMemo(() => tunnelSubscription?.userKnowledge, [tunnelSubscription]);

    const knowledgeOrigin = useMemo(() => {
        const origin = jsonParseSafe(userKnowledge?.knowledgeOrigin, {});
        if (origin.other) {
            origin.choice = 'general_options.other_2';
        }
        return origin;
    }, [userKnowledge]);
    const listedShares = useMemo(
        () => jsonParseSafe(userKnowledge?.listedShares, []),
        [userKnowledge]
    );
    const unlistedShares = useMemo(
        () => jsonParseSafe(userKnowledge?.unlistedShares, []),
        [userKnowledge]
    );
    const opcvm = useMemo(() => jsonParseSafe(userKnowledge?.opcvm, []), [userKnowledge]);
    const scpi = useMemo(() => jsonParseSafe(userKnowledge?.scpi, []), [userKnowledge]);
    const privateEquityFund = useMemo(
        () => jsonParseSafe(userKnowledge?.privateEquityFund, []),
        [userKnowledge]
    );

    const defaultData = useMemo(
        () => ({
            longTermFund: hasSubmittedOnce ? userKnowledge?.longTermFund : null,
            capitalGuarantee: hasSubmittedOnce ? userKnowledge?.capitalGuarantee : null,
            fundIntention: hasSubmittedOnce ? userKnowledge?.fundIntention : null,
            fundPerformance: hasSubmittedOnce ? userKnowledge?.fundPerformance : null,
            futurePerformanceIndex: hasSubmittedOnce ? userKnowledge?.futurePerformanceIndex : null,
            fundAdmission: hasSubmittedOnce ? userKnowledge?.fundAdmission : null,
            fundRisk: hasSubmittedOnce ? userKnowledge?.fundRisk : null,
            assetDiversification: hasSubmittedOnce ? userKnowledge?.assetDiversification : null,
            listedShares: hasSubmittedOnce ? listedShares : null,
            unlistedShares: hasSubmittedOnce ? unlistedShares : null,
            opcvm: hasSubmittedOnce ? opcvm : null,
            scpi: hasSubmittedOnce ? scpi : null,
            privateEquityFund: hasSubmittedOnce ? privateEquityFund : null,
            knowledgeOrigin: {
                choice: hasSubmittedOnce ? knowledgeOrigin?.choice : null,
                other: hasSubmittedOnce ? knowledgeOrigin?.other || '' : '',
            },
        }),
        [
            userKnowledge,
            hasSubmittedOnce,
            knowledgeOrigin,
            listedShares,
            unlistedShares,
            opcvm,
            scpi,
            privateEquityFund,
        ]
    );

    const [data, setData] = useState(defaultData);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const [currentForm, setCurrentForm] = useState(0);
    const [formStatus, setFormStatus] = useState({
        0: {
            status: 'started',
            isDirty: false,
            errors: [],
        },
        1: {
            status: 'notStarted',
            isDirty: false,
            errors: [],
        },
        2: {
            status: 'notStarted',
            isDirty: false,
            errors: [],
        },
    });

    const mappings = useMemo(
        () => ({
            group_1: {
                question_1: 'longTermFund',
                question_2: 'capitalGuarantee',
                question_3: 'fundIntention',
                question_4: 'fundPerformance',
            },
            group_2: {
                question_5: 'futurePerformanceIndex',
                question_6: 'fundAdmission',
                question_7: 'fundRisk',
                question_8: 'assetDiversification',
            },
            group_3: {
                question_10: 'listedShares',
                question_11: 'unlistedShares',
                question_12: 'opcvm',
                question_13: 'scpi',
                question_14: 'privateEquityFund',
                question_15: 'knowledgeOrigin',
            },
        }),
        []
    );

    const schema = useMemo(
        () =>
            tunnelSubscription.indirectUntransferred
                ? {}
                : {
                      0: {
                          longTermFund: Joi.string().required().messages({
                              'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                              'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                          }),
                          capitalGuarantee: Joi.string().required().messages({
                              'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                              'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                          }),
                          fundIntention: Joi.string().required().messages({
                              'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                              'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                          }),
                          fundPerformance: Joi.string().required().messages({
                              'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                              'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                          }),
                      },
                      1: {
                          futurePerformanceIndex: Joi.string().required().messages({
                              'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                              'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                          }),
                          fundAdmission: Joi.string().required().messages({
                              'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                              'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                          }),
                          fundRisk: Joi.string().required().messages({
                              'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                              'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                          }),
                          assetDiversification: Joi.string().required().messages({
                              'string.base': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                              'string.empty': texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION,
                          }),
                      },
                  },
        [tunnelSubscription]
    );

    const getForm = useCallback(
        formId => {
            const section = Object.keys(mappings)[formId];
            const formProps = Object.keys(mappings[section]).map(key => mappings[section][key]);
            return Object.keys(data)
                .filter(key => formProps.includes(key))
                .reduce((obj, key) => {
                    obj[key] = data[key];
                    return obj;
                }, {});
        },
        [data, mappings]
    );

    const getNotStartedForms = useCallback(() => {
        return Object.keys(formStatus).filter(form => formStatus[form].status === 'notStarted');
    }, [formStatus]);

    const currentFormIsValid = useCallback(
        (form, formIndex, displayErrors = true) => {
            const validationSchema = schema[formIndex];
            const { valid, newErrors } = validateSchema(validationSchema, form);
            let isValid = valid;
            let errors = Object.values(newErrors);

            if (formIndex === 2) {
                if (!data.knowledgeOrigin.choice) {
                    isValid = false;
                    errors.push(texts.VOUS_DEVEZ_SELECTIONNER_UNE_OPTION);
                } else if (
                    data.knowledgeOrigin.choice === 'general_options.other_2' &&
                    data.knowledgeOrigin.other === ''
                ) {
                    isValid = false;
                    errors.push(texts.VOUS_DEVEZ_REMPLIR_LE_CHAMP);
                }
            }

            if (displayErrors) {
                setFormStatus(prevState => ({
                    ...prevState,
                    [formIndex]: {
                        ...prevState[formIndex],
                        errors: isValid ? [] : errors,
                    },
                }));
            }
            return isValid;
        },
        [schema, data]
    );

    const handleEditFormBox = useCallback(
        ({ currentTarget: { id } }) => {
            const formId = parseInt(id, 10);
            const form = getForm(currentForm);

            if (currentFormIsValid(form, currentForm)) {
                setFormStatus(prevState => {
                    if (formId !== currentForm) {
                        return {
                            ...prevState,
                            [formId]: { ...prevState[formId], status: 'started', isDirty: true },
                            [currentForm]: {
                                ...prevState[currentForm],
                                status: 'completed',
                                isDirty: true,
                            },
                        };
                    } else {
                        return {
                            ...prevState,
                            [formId]: { ...prevState[formId], status: 'started', isDirty: true },
                        };
                    }
                });
                setCurrentForm(formId);
            }
        },
        [currentForm, currentFormIsValid, getForm]
    );

    const handleFormBox = useCallback(
        ({ currentTarget: { id } }) => {
            const formId = parseInt(id, 10);
            const notStartedForm = getNotStartedForms()[0];
            const notStartedFormId = parseInt(notStartedForm || -1, 10);

            const form = getForm(formId);

            if (currentFormIsValid(form, formId)) {
                setCurrentForm(notStartedFormId !== -1 ? notStartedFormId : formId + 1);
                setFormStatus(prevState => {
                    if (notStartedFormId !== -1) {
                        return {
                            ...prevState,
                            [formId]: {
                                ...prevState[formId],
                                status: 'completed',
                                isDirty: true,
                            },
                            [notStartedFormId]: {
                                ...prevState[notStartedFormId],
                                status: 'started',
                                isDirty: false,
                            },
                        };
                    } else {
                        return {
                            ...prevState,
                            [formId]: {
                                ...prevState[formId],
                                status: 'completed',
                                isDirty: true,
                            },
                            [formId + 1]: {
                                ...prevState[formId + 1],
                                status: 'started',
                                isDirty: true,
                            },
                        };
                    }
                });
            }
        },
        [currentFormIsValid, getForm, getNotStartedForms]
    );

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            sendUserKnowledge({
                payload: data,
                callback: () => {
                    if (afterSubmitCallback) {
                        afterSubmitCallback();
                    } else {
                        handleNextStep(true);
                    }
                },
            });
        },
        [sendUserKnowledge, data, handleNextStep]
    );

    // this function returns true if the step has suffered changes and false otherwise
    const checkChanges = useCallback(() => {
        return !isEqual(defaultData, data);
    }, [data, defaultData]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(() => setData(defaultData), [defaultData]);

    useEffect(() => {
        getCurrentUser();
    }, [getCurrentUser]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, isResubmit),
        [handleSubmit, checkChanges, isResubmit, setTunnelChangesHandlers]
    );

    useEffect(() => {
        let valid = true;
        for (let index = 0; index < Object.keys(mappings).length; index++) {
            const form = getForm(index);
            if (!currentFormIsValid(form, index, false)) {
                valid = false;
                setIsSubmitDisabled(true);
                break;
            }
        }
        if (valid || tunnelSubscription?.indirectUntransferred) {
            setIsSubmitDisabled(false);
        }
    }, [currentFormIsValid, getForm, mappings, tunnelSubscription]);

    useEffect(() => {
        if (Object.values(defaultData).every(val => !!val)) {
            Object.keys(mappings).forEach((item, index) => {
                setFormStatus(prevState => ({
                    ...prevState,
                    [index]: { ...prevState[index], status: 'completed', isDirty: true },
                }));
            });
        }
    }, [defaultData, mappings]);

    const handleChange = useCallback(
        e => {
            const { name, value } = e.target;
            const { section, question } = extractSectionAndQuestion(name);
            const key = mappings[section][question];
            setData(prevState => {
                if (key === 'knowledgeOrigin') {
                    return { ...prevState, knowledgeOrigin: { choice: value, other: '' } };
                }
                return {
                    ...prevState,
                    [key]: value,
                };
            });
        },
        [mappings]
    );

    const handleQuestion9Change = useCallback(
        e => {
            const { name, value } = e.target;
            const { section, question } = extractSectionAndQuestion(name);
            const key = mappings[section][question];
            setData(prevState => {
                let newOptions = [];
                const oldOptions = prevState[key] || [];
                if (oldOptions.includes(value)) {
                    // here remove option from array
                    const index = oldOptions.indexOf(value);
                    newOptions = [...oldOptions.slice(0, index), ...oldOptions.slice(index + 1)];
                } else {
                    // here add the option to array
                    newOptions = [...oldOptions, value];
                }
                return { ...prevState, [key]: newOptions };
            });
        },
        [mappings]
    );

    const handleKnowledgeOriginOtherChange = useCallback(e => {
        const { value } = e.target;
        setData(prevState => ({
            ...prevState,
            knowledgeOrigin: { choice: 'general_options.other_2', other: value },
        }));
    }, []);

    const renderQuestion = question => {
        const { section, question: questionName } = extractSectionAndQuestion(question.key);

        const value =
            questionName === 'question_15'
                ? data[mappings[section][questionName]].choice
                : data[mappings[section][questionName]];
        const otherValue =
            questionName === 'question_15' ? data[mappings[section][questionName]].other : null;

        const hasInfoButton = ({ infoTitle, infoContent }) => {
            return infoTitle !== '' && infoContent !== '';
        };

        return (
            <FormControl component="fieldset" key={question.key}>
                <label className={classes.questionLabel}>{question.value}</label>
                {question.rows && question.rows.length > 0 ? (
                    question.rows.map(row => (
                        <FormGroup key={row.key} className={classes.instrumentsContainer}>
                            <Typography
                                className={clsx({
                                    [classes.label]: true,
                                    [classes.option]: true,
                                    [classes.withTopPadding]:
                                        row.key === 'step35.group_3.question_14',
                                })}
                                variant="body1"
                                gutterBottom
                            >
                                {row.value}
                                {hasInfoButton(row) && (
                                    <InfoButton
                                        title={row.infoTitle}
                                        content={row.infoContent}
                                        disableMarginBottom
                                    />
                                )}
                            </Typography>
                            <div className={classes.optionsContainer}>
                                {row.elements.map(el => {
                                    return (
                                        <Box key={el.key}>
                                            <CheckboxLabel
                                                name={row.key}
                                                className={classes.option}
                                                value={el.key}
                                                onChange={handleQuestion9Change}
                                                label={el.value}
                                                checked={
                                                    !!data[mappings[section][el.parent]]?.includes(
                                                        el.key
                                                    )
                                                }
                                            />
                                        </Box>
                                    );
                                })}
                            </div>
                        </FormGroup>
                    ))
                ) : (
                    <RadioGroup name={question.key} className={classes.flexContainer} value={value}>
                        {question.options.map(option => {
                            return (
                                <Box key={option.key} className={classes.question}>
                                    <RadioLabel
                                        key={option.key}
                                        value={option.key}
                                        label={option.value}
                                        labelPlacement="end"
                                        onChange={handleChange}
                                    />
                                    {option.key === 'general_options.other_2' && (
                                        <CustomTextField
                                            inputStyle={classes.otherInput}
                                            value={otherValue}
                                            onChange={handleKnowledgeOriginOtherChange}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </RadioGroup>
                )}
            </FormControl>
        );
    };

    const stepSubtitle = useMemo(() => {
        const baseMessage = isSLP
            ? texts.USER_KNOWLEDGE_SUBTITILE_SLP
            : texts.USER_KNOWLEDGE_SUBTITILE;
        return isCompany ? (
            <React.Fragment>
                <Typography variant="subtitle1" style={{ marginBottom: 20 }}>
                    {baseMessage}
                </Typography>
                <Typography variant="subtitle1">{texts.USER_KNOWLEDGE_DISCLAIMER}</Typography>
            </React.Fragment>
        ) : (
            baseMessage
        );
    }, [isCompany, isSLP]);

    return (
        <TunnelStep title={texts.USER_KNOWLEDGE} subtitle={stepSubtitle}>
            <Loader loading={userLoading}>
                <form className={classes.form} onSubmit={e => e.preventDefault()}>
                    {sections.map((section, index) => (
                        <FormBox
                            key={section.key}
                            handleEditFormBox={handleEditFormBox}
                            handleFormBox={handleFormBox}
                            formId={index}
                            isLast={index === sections.length - 1}
                            status={formStatus[index].status}
                            label={section.value}
                            largeLabel
                            completedLabel={section.value}
                            formLength={sections.length - 1}
                            showInfoButton={false}
                        >
                            {section.questions.map(renderQuestion)}
                            {formStatus[index].errors[0] && (
                                <ErrorMessage message={formStatus[index].errors[0]} />
                            )}
                        </FormBox>
                    ))}
                    <SubmitButton
                        text={isResubmit && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                        disabled={isSubmitDisabled || buttonLoader}
                        loading={buttonLoader}
                        onClick={changesMade || !hasStatusOk ? handleSubmit() : handleNextStep}
                    />
                </form>
            </Loader>
        </TunnelStep>
    );
};

export default UserKnowledge;
